<script setup lang="ts">
import BottomSheet, { type BottomSheetPosition } from '@/components/layout/BottomSheet.vue';
import SheetItineraryButton from '@/components/layout/SheetItineraryButton.vue';
import HomeNextDepartures from './HomeNextDepartures.vue';
import { useBottomSheetAnchor } from '@/use/bottom-sheet-anchor';
import { AppRoute } from '@/libs/routing';
import { MapFocusType, useAppStore, type AppLoadingState } from '@/stores/app';
import { useGtfsStore } from '@/stores/gtfs';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const appStore = useAppStore();
const gtfsStore = useGtfsStore();
const router = useRouter();

const sheetPosition = ref<BottomSheetPosition>('intermediate');
const sheetAnchor = useBottomSheetAnchor();

const loadingState = computed<boolean | AppLoadingState>(() => appStore.isLoading);

watch(
  () => loadingState.value,
  () => {
    const isGtfsLoading = typeof loadingState.value == 'boolean' ? false : loadingState.value.gtfs;
    if (!isGtfsLoading) setMapFocus();
  },
);

onMounted(() => {
  setMapFocus();
});

function setMapFocus() {
  appStore.setMapFocus({
    type: MapFocusType.BOUNDS,
    elem: gtfsStore.networkMapBounds,
  });
}

function onSheetHeight(height: number, dragging: boolean) {
  sheetAnchor.onHeight(height, dragging);
  appStore.setMapBottomInset(height, dragging);
}

function goToItinerary() {
  router.push({ name: AppRoute.ITINERARY });
}
</script>

<template>
  <div class="home-panel">
    <div class="home-panel__sheet-area">
      <SheetItineraryButton :anchor="sheetAnchor" @click="goToItinerary" />

      <BottomSheet
        v-model:position="sheetPosition"
        @update:height="onSheetHeight"
        @update:intermediateHeight="sheetAnchor.onIntermediateHeight"
      >
        <template #header="{ scrolled }">
          <h2 class="home-panel__sheet-title" :class="{ 'home-panel__sheet-title--scrolled': scrolled }">
            {{ $t('home.nextDepartures') }}
          </h2>
        </template>

        <HomeNextDepartures />
      </BottomSheet>
    </div>
  </div>
</template>

<style lang="scss">
.home-panel {
  bottom: 0;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;

  &__sheet-area {
    bottom: 0;
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    // A hair below the search bar: full screen, the sheet must not slide under it.
    top: calc(var(--search-top-bar-height) + 4px + var(--status-bar-band));
  }

  &__sheet-title {
    // Transparent rather than none: the border must not shift the content when it appears.
    border-bottom: 1px solid transparent;
    color: $color-neutral-highest;
    // Same ad-hoc large title as the stop sheet: the design system has no
    // heading token this size yet.
    font: 600 24px / 32px $font-poppins;
    letter-spacing: $letter-spacing-tight;
    margin: 0;
    padding: $spacing-2 $spacing-5 $spacing-4;
    transition: border-color 0.3s ease;

    &--scrolled {
      border-color: $color-border-medium;
    }
  }
}
</style>
